/**
 * A day's band as a colour and a word — the ONLY place `DayBand` becomes either.
 *
 * The calendar cell, the legend beneath it, the coverage bar in the month summary and the tag beside
 * the day heading all read this map. They are four renderings of one verdict, and the moment any of
 * them hard-codes `v2.warnWash` for a band, they can start disagreeing about what amber means.
 *
 * The `label` is the band said out loud. It carries the threshold where the threshold is the point —
 * the legend has to teach the rule, or the tints are folklore.
 */

import { DAY_FAR_OVER, DAY_ON_TARGET, type DayBand } from "../builder/targetsMath";
import { v2 } from "./v2Tokens";

export type BandTone = {
  /** the cell's wash */
  fill: string;
  /** the number and the percentage on it */
  text: string;
  /** what the band means, for the legend and for screen readers */
  label: string;
};

export const bandTone: Record<DayBand, BandTone> = {
  ok: { fill: v2.brandWash, text: v2.brandText, label: `ביעד — עד ±${DAY_ON_TARGET}% מהיעד` },
  warn: { fill: v2.warnWash, text: v2.warn, label: "מעל היעד" },
  over: { fill: v2.overWash, text: v2.over, label: `חריגה מעל ${DAY_FAR_OVER}%` },
  under: { fill: v2.underWash, text: v2.under, label: "מתחת ליעד" },
  // A day with no goals cannot be on target or off it, so it gets no wash at all — a neutral cell
  // that still shows its number.
  none: { fill: v2.surface, text: v2.ink2, label: "אין יעד ליום זה" },
};

/** The short form, for the tag beside the day heading — no threshold, just the verdict. */
export const bandWord: Record<DayBand, string> = {
  ok: "ביעד",
  warn: "מעל היעד",
  over: "חריגה",
  under: "מתחת ליעד",
  none: "ללא יעד",
};
